import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CartProvider, useCart } from "@/contexts/CartContext";
import CartDrawer from "@/components/CartDrawer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import Pagination from "@/components/Pagination";
import { useResponsivePageSize } from "@/hooks/useResponsivePageSize";
import { Plus, Package, ChevronRight, Flame, QrCode, Menu, Search, X, Sparkles } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { BundleTicker, BundleOverlay, BundleUpsellOverlay } from "@/components/BundleShop";
import TrendingBanner from "@/components/TrendingBanner";
import { useAuth } from "@/contexts/AuthContext";
import { isReadOnlyGuest } from "@/lib/roles";

function StoreBody({ storefront, slug }) {
  const { add, addBundle, setContext } = useCart();
  const { user } = useAuth();
  const readOnlyGuest = isReadOnlyGuest(user);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [bundles, setBundles] = useState([]);
  const [openBundle, setOpenBundle] = useState(null);
  const [upsell, setUpsell] = useState(null);
  const [trendingIds, setTrendingIds] = useState(new Set());
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const pageSize = useResponsivePageSize(6, 12);
  const handleTrendingIds = React.useCallback((ids) => setTrendingIds(new Set(ids)), []);
  const inverted = !!storefront.is_self_enrolled;
  const isPartnerPortal = !!storefront.is_distributing_partner_portal;
  const initials = (storefront.client.name || storefront.client.company || "Shop").trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
  const ownerPhoto = storefront.client.photo_url
    ? (String(storefront.client.photo_url).startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${storefront.client.photo_url}` : storefront.client.photo_url)
    : null;
  const portalUrl = `${window.location.origin}/shop/${slug}`;

  useEffect(() => { setContext("retail"); }, []); // eslint-disable-line
  const categories = useMemo(() => {
    const values = new Set();
    storefront.products.forEach((product) => (product.categories || "").split(",").map((item) => item.trim()).filter(Boolean).forEach((item) => values.add(item)));
    return Array.from(values).sort();
  }, [storefront.products]);
  const filteredProducts = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return storefront.products.filter((product) => {
      if (needle && !`${product.name} ${product.sku || ""} ${product.description || ""}`.toLowerCase().includes(needle)) return false;
      return !category || (product.categories || "").toLowerCase().includes(category.toLowerCase());
    });
  }, [storefront.products, query, category]);
  useEffect(() => { setPage(0); }, [query, category]);
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / pageSize));
  const pagedProducts = filteredProducts.slice(page * pageSize, (page + 1) * pageSize);
  const categoryCount = (name) => storefront.products.filter((product) => (product.categories || "").toLowerCase().includes(name.toLowerCase())).length;
  useEffect(() => {
    api.get(`/retail/${slug}/orders`)
      .then((r) => setOrders(r.data))
      .catch(() => setOrders([]))
      .finally(() => setOrdersLoading(false));
    // Bundles visible on THIS sub-portal only (clients_only + assigned-to-me)
    api.get(`/bundles/for-slug/${slug}`).then((r) => setBundles(r.data)).catch(() => setBundles([]));
  }, [slug]);

  // product_id → first bundle covering it (drives the upsell overlay)
  const bundleByProduct = useMemo(() => {
    const map = new Map();
    bundles.forEach((b) => (b.products || []).forEach((p) => {
      if (!map.has(p.id)) map.set(p.id, b);
    }));
    return map;
  }, [bundles]);

  const handleProductClick = (product) => {
    const b = bundleByProduct.get(product.id);
    if (b) {
      setUpsell({ product, bundle: b });
    } else {
      add(product, 1);
      toast.success(`Added ${product.name}`);
    }
  };

  return (
    <div className={`min-h-screen ${inverted ? "bg-[var(--ink)] text-white" : "bg-[var(--bg)]"}`} data-testid={inverted ? "substore-inverted" : "substore-standard"}>
      <header className={`sticky top-0 z-10 border-b ${inverted ? "bg-black border-black/40" : "bg-white border-[var(--border)]"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 sm:py-5 flex items-center justify-between gap-3 sm:gap-6">
          <div className="flex min-w-0 items-center gap-3 sm:gap-4">
            <Link to="/login" className="flex shrink-0 items-center gap-2.5 hover:opacity-80 transition-opacity" data-testid="retail-logo-link">
              <img src="/brand/mpire-logo.png" alt="MPIRE" className="w-[4.125rem] h-[4.125rem] sm:w-[4.5rem] sm:h-[4.5rem]" />
              <span className="flex flex-col leading-none">
                <span className={`font-bold tracking-tight text-base ${inverted ? "text-white" : "text-[var(--ink)]"}`} data-testid="retail-app-name">MPower!</span>
                <span className="mt-1 text-[8px] uppercase tracking-[0.14em] text-gray-500 whitespace-nowrap" data-testid="retail-powered-by">BY 2CONMEDIA.COM</span>
              </span>
            </Link>
            <div className="h-[4.125rem] w-[4.125rem] sm:h-[4.5rem] sm:w-[4.5rem] shrink-0 overflow-hidden rounded-full border-2 border-[var(--brand)] bg-[var(--brand)]/10" data-testid="substore-owner-avatar">
              {ownerPhoto ? <img src={ownerPhoto} alt={storefront.client.name} className="h-full w-full object-cover" data-testid="substore-owner-photo" /> : <span className="flex h-full w-full items-center justify-center text-xs font-extrabold text-[var(--brand)]" data-testid="substore-owner-initials">{initials}</span>}
            </div>
            <div className={`hidden min-w-0 border-l pl-4 sm:block ${inverted ? "border-white/20" : "border-[var(--border)]"}`} data-testid="retail-store-identity">
              <p className={`label-eyebrow truncate ${inverted ? "text-[var(--brand)]" : "text-[var(--brand)]"}`}>{isPartnerPortal ? "Distributing Partner sub-portal" : (storefront.client.company || "Reseller")}</p>
              <h1 className={`truncate text-2xl font-bold tracking-tight capitalize ${inverted ? "text-white" : ""}`}>{storefront.client.name}'s shop</h1>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button type="button" onClick={() => setMenuOpen(true)} className={`min-h-10 min-w-10 inline-flex items-center justify-center rounded-md border transition-colors ${inverted ? "border-white/20 text-white hover:bg-white/10" : "border-[var(--border)] text-[var(--ink)] hover:bg-[var(--bg-soft)]"}`} aria-label="Browse product categories" data-testid="substore-menu-button"><Menu size={19} /></button>
            <CartDrawer
            clientEmailPrefill={storefront.client.email || ""}
            clientNamePrefill={storefront.client.name || ""}
            clientId={storefront.client.id}
            portalSlug={slug}
            source="client_portal"
            triggerClassName="bg-[var(--brand)] hover:bg-[#8d1820] text-white border-[var(--brand)] shadow-[0_3px_12px_rgba(176,30,36,0.3)] font-bold"
            badgeClassName="bg-white text-[var(--brand)] border-2 border-[var(--brand)]"
            />
          </div>
        </div>
        <div className={`border-t ${inverted ? "border-white/10 bg-black/40" : "border-[var(--border)] bg-white"}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 sm:py-4 flex items-center gap-3">
            <div className="relative flex-1 max-w-2xl mx-auto">
              <Search size={18} className="absolute left-4 top-3.5 text-gray-400" />
              <Input value={query} onChange={(event) => setQuery(event.target.value)} className={`pl-11 h-11 sm:h-12 text-base ${inverted ? "bg-white/5 border-white/20 text-white placeholder:text-white/50" : "bg-[var(--bg-soft)]"}`} placeholder="Search products…" data-testid="substore-search" />
            </div>
            {category && <button type="button" onClick={() => setCategory("")} className={`text-xs font-semibold whitespace-nowrap ${inverted ? "text-amber-300" : "text-[var(--brand)]"}`} data-testid="substore-clear-category">Clear</button>}
          </div>
        </div>
      </header>

      {menuOpen && <>
        <button aria-label="Close product categories" className="fixed inset-0 z-30 bg-black/45" onClick={() => setMenuOpen(false)} data-testid="substore-menu-backdrop" />
        <aside className={`fixed inset-y-0 left-0 z-40 w-[min(18rem,88vw)] overflow-y-auto border-r p-4 shadow-2xl ${inverted ? "bg-[#101116] border-white/10 text-white" : "bg-white border-[var(--border)]"}`} data-testid="substore-category-menu">
          <div className="flex items-center justify-between border-b border-current/10 pb-3"><p className="label-eyebrow">Browse categories</p><button type="button" onClick={() => setMenuOpen(false)} className="min-h-10 min-w-10 inline-flex items-center justify-center" aria-label="Close menu" data-testid="substore-menu-close"><X size={19} /></button></div>
          <div className="mt-3 space-y-1">
            <button type="button" onClick={() => { setCategory(""); setMenuOpen(false); }} className={`w-full min-h-11 px-3 flex items-center gap-2 rounded-md text-left text-sm ${!category ? "bg-[var(--brand)] text-white" : inverted ? "hover:bg-white/10" : "hover:bg-[var(--bg-soft)]"}`} data-testid="substore-category-all"><Sparkles size={14} /><span className="flex-1">All products</span><span className="tabular-nums text-xs">{storefront.products.length}</span></button>
            {categories.map((item) => <button type="button" key={item} onClick={() => { setCategory(item); setMenuOpen(false); }} className={`w-full min-h-11 px-3 flex items-center rounded-md text-left text-sm capitalize ${category === item ? "bg-[var(--brand)] text-white" : inverted ? "hover:bg-white/10" : "hover:bg-[var(--bg-soft)]"}`} data-testid={`substore-category-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}><span className="flex-1 truncate">{item}</span><span className="tabular-nums text-xs">{categoryCount(item)}</span></button>)}
          </div>
        </aside>
      </>}

      <section className={`border-b ${inverted ? "border-white/10 bg-black/30" : "border-[var(--border)] bg-[var(--bg-soft)]"}`} data-testid="substore-share-strip">
        <div className="mx-auto flex max-w-7xl items-center gap-4 sm:gap-5 px-4 sm:px-8 py-4 sm:py-5">
          <div className="rounded-md bg-white p-2 shadow-sm"><QRCodeSVG value={portalUrl} size={68} level="M" bgColor="#ffffff" fgColor="#B01E24" data-testid="substore-share-qr" /></div>
          <div className="min-w-0"><p className={`flex items-center gap-1.5 text-sm font-bold ${inverted ? "text-white" : "text-[var(--ink)]"}`}><QrCode size={16} /> Share this shop</p><p className={`mt-1 truncate text-xs sm:text-sm ${inverted ? "text-white/60" : "text-gray-500"}`}>Scan to save or share {storefront.client.name}'s unique sub-store.</p></div>
        </div>
      </section>

      {/* Dynamic Bundles ticker — only bundles visible to this sub-portal */}
      {bundles.length > 0 && (
        <BundleTicker bundles={bundles} onOpenBundle={(b) => setOpenBundle(b)} />
      )}


      <main className="max-w-6xl mx-auto p-6 lg:p-10">
        <TrendingBanner
          priceKind="retail"
          tone={inverted ? "dark" : "brand"}
          onLoaded={handleTrendingIds}
        />
        {storefront.products.length === 0 ? (
          <p className={`text-center py-16 ${inverted ? "text-gray-400" : "text-gray-500"}`}>
            {inverted ? "Deals dropping soon — check back in a bit." : "This reseller has not published any items yet. Approved retail prices will appear here."}
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="retail-grid">
            {pagedProducts.map((p) => (
              <div
                key={p.id}
                className={`rounded-lg overflow-hidden hover:shadow-lg transition-shadow group border relative ${inverted ? "bg-[#1a1a1a] border-white/10 text-white" : "bg-white border-[var(--border)]"}`}
                data-testid={`retail-product-${p.id}`}
              >
                {trendingIds.has(p.id) && (
                  <span
                    className={`absolute z-10 rounded-full bg-gradient-to-br from-red-500 to-orange-500 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white shadow-sm flex items-center gap-0.5 ${bundleByProduct.has(p.id) ? "top-8 left-1.5" : "top-1.5 left-1.5"}`}
                    data-testid={`retail-trending-badge-${p.id}`}
                    title="One of this week's top sellers"
                  >
                    <Flame size={9} strokeWidth={2.75} /> Trending
                  </span>
                )}
                {bundleByProduct.has(p.id) && (
                  <span
                    className="absolute top-1.5 left-1.5 z-10 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white shadow-sm"
                    data-testid={`retail-bundle-badge-${p.id}`}
                    title="Part of a bundle deal"
                  >
                    Bundle
                  </span>
                )}
                <div className={`aspect-square overflow-hidden ${inverted ? "bg-black/40" : "bg-[var(--bg-soft)]"}`}>
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  ) : <div className={`w-full h-full flex items-center justify-center text-4xl ${inverted ? "text-white/20" : "text-gray-300"}`}>·</div>}
                </div>
                <div className="p-4">
                  <h3 className={`font-semibold capitalize leading-tight line-clamp-2 min-h-[40px] ${inverted ? "text-white" : ""}`}>{p.name}</h3>
                  <p className={`text-xs line-clamp-1 ${inverted ? "text-white/60" : "text-gray-500"}`}>{p.description}</p>
                  <div className="mt-3 flex items-center justify-between">
                    <div>
                      <div className={`text-xl font-bold tabular-nums ${inverted ? "text-white" : ""}`}>${Number(p.retail_price).toFixed(2)}</div>
                      {inverted && p.original_price && p.original_price > p.retail_price && (
                        <div className="text-xs text-white/50 line-through tabular-nums">${Number(p.original_price).toFixed(2)}</div>
                      )}
                    </div>
                    <Button size="sm" onClick={() => handleProductClick(p)} disabled={readOnlyGuest} className="btn-brand h-8" data-testid={`retail-add-${p.id}`}>
                      <Plus size={12} className="mr-1" /> {readOnlyGuest ? "View only" : "Add"}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {storefront.products.length > 0 && <div className={`mt-6 overflow-hidden rounded-md border ${inverted ? "border-white/10 bg-black/30" : "border-[var(--border)] bg-white"}`}><Pagination page={page} totalPages={totalPages} onPageChange={setPage} totalItems={filteredProducts.length} pageSize={pageSize} testid="substore-pagination" /></div>}

        {/* Order history — only for this client, from this portal */}
        <section className="mt-16">
          <div className="flex items-center gap-2 mb-4">
            <Package size={16} className="text-[var(--brand)]" />
            <h2 className={`text-2xl font-bold tracking-tight ${inverted ? "text-white" : ""}`}>Your order history</h2>
          </div>
          <p className={`text-xs mb-4 ${inverted ? "text-white/60" : "text-gray-500"}`}>
            Only orders placed through this portal are shown here.
          </p>
          {ordersLoading ? (
            <p className={inverted ? "text-white/50" : "text-gray-400"}>Loading…</p>
          ) : orders.length === 0 ? (
            <Card className={`p-8 text-center ${inverted ? "bg-[#1a1a1a] text-white/60 border-white/10" : "bg-white text-gray-500"}`} data-testid="no-portal-orders">
              No orders yet. Your purchases will appear here after checkout.
            </Card>
          ) : (
            <Card className={`p-0 overflow-hidden ${inverted ? "bg-[#1a1a1a] border-white/10" : "bg-white"}`} data-testid="portal-orders">
              <table className={`w-full text-sm ${inverted ? "text-white" : ""}`}>
                <thead className={`text-xs uppercase tracking-wide ${inverted ? "bg-black/40 text-white/60" : "bg-[var(--bg-soft)] text-gray-500"}`}>
                  <tr>
                    <th className="text-left p-3">Date</th>
                    <th className="text-left p-3">Items</th>
                    <th className="text-right p-3">Total</th>
                    <th className="text-left p-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} className={`border-t ${inverted ? "border-white/10" : "border-[var(--border)]"}`} data-testid={`portal-order-${o.id}`}>
                      <td className="p-3 whitespace-nowrap">
                        {o.created_at?.slice(0, 10)}
                        <div className={`text-[10px] ${inverted ? "text-white/40" : "text-gray-400"}`}>{o.created_at?.slice(11, 16)}</div>
                      </td>
                      <td className="p-3">
                        <ul className="text-xs space-y-0.5">
                          {(o.products || []).slice(0, 3).map((it, i) => (
                            <li key={i} className="capitalize">
                              <span className={inverted ? "text-white/50" : "text-gray-500"}>×{it.qty}</span> {it.name}
                            </li>
                          ))}
                          {(o.products || []).length > 3 && (
                            <li className={inverted ? "text-white/40" : "text-gray-400"}>+ {(o.products || []).length - 3} more</li>
                          )}
                        </ul>
                      </td>
                      <td className="p-3 text-right font-semibold tabular-nums">${Number(o.total || 0).toFixed(2)}</td>
                      <td className="p-3">
                        <span className="status-pill bg-emerald-100 text-emerald-800 border-emerald-200 capitalize">{o.status || "paid"}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </section>
      </main>

      <footer className={`mt-16 border-t py-6 text-center text-xs ${inverted ? "border-white/10 text-white/50" : "border-[var(--border)] text-gray-500"}`}>
        Secure checkout by Stripe · Powered by MPIRE Distribution
      </footer>

      {/* Bundle overlays — mirror /shop behavior on the sub-portal */}
      <BundleOverlay
        bundle={openBundle}
        onClose={() => setOpenBundle(null)}
        onAddBundle={(b) => addBundle(b)}
      />
      <BundleUpsellOverlay
        product={upsell?.product}
        bundle={upsell?.bundle}
        onClose={() => setUpsell(null)}
        onJustThisItem={() => {
          add(upsell.product, 1);
          toast.success(`Added ${upsell.product.name}`);
          setUpsell(null);
        }}
        onUpgradeToBundle={() => {
          addBundle(upsell.bundle);
          toast.success(`"${upsell.bundle.name}" added to cart`);
          setUpsell(null);
        }}
      />
    </div>
  );
}

export default function RetailSubPortal() {
  const { slug } = useParams();
  const [storefront, setStorefront] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/retail/${slug}`).then((r) => setStorefront(r.data))
       .catch((e) => setErr(e.response?.data?.detail || "Storefront not found"));
  }, [slug]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-gray-500">{err}</div>;
  if (!storefront) return <div className="min-h-screen flex items-center justify-center text-gray-400">Loading…</div>;

  return <CartProvider><StoreBody storefront={storefront} slug={slug} /></CartProvider>;
}
